// /mensajes — the client's two-way thread with their INHERTA account manager
// ("Mensajes con tu gerente"). Replaces the dead comingSoon "Escríbele a tu
// gerente" buttons on Home / Settings (billing) / Onboarding.
//
// Data via the concierge-data seam: getConciergeThread() loads the thread (and
// marks the manager's messages read on the server), sendConciergeMessage() posts
// a reply. Sending is optimistic then refetched; a failed send is rolled back and
// surfaced with toast.error. Opening the page refreshes the nav badge (the GET
// cleared unread), so we invalidate the summary query the shell polls.

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useEffect } from 'react'

import {
  type ConciergeMessage,
  conciergeInitials,
  getConciergeThread,
  sendConciergeMessage,
} from './concierge-data.js'
import { ConciergeThread } from './ConciergeThread.js'
import { PageHeader, Spinner, toast } from '../../design-system/index.js'

export function ConciergePage(): JSX.Element {
  const qc = useQueryClient()
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['concierge', 'thread'],
    queryFn: getConciergeThread,
    // Poll while the thread is open so replies + read-status appear without a reload.
    refetchInterval: 5000,
  })

  // The GET marked the manager's messages read: refresh the shell's unread badge.
  const loaded = data !== undefined
  useEffect(() => {
    if (loaded) void qc.invalidateQueries({ queryKey: ['concierge', 'summary'] })
  }, [loaded, qc])

  const sendMutation = useMutation({
    mutationFn: sendConciergeMessage,
    onError: () => {
      toast.error('No se pudo enviar', { description: 'Inténtalo de nuevo.' })
    },
    onSettled: () => {
      void qc.invalidateQueries({ queryKey: ['concierge', 'thread'] })
    },
  })

  if (isLoading) {
    return (
      <div data-testid="concierge" className="mx-auto w-full max-w-3xl py-10">
        <Spinner label="Cargando mensajes…" />
      </div>
    )
  }

  if (isError || !data) {
    return (
      <div data-testid="concierge" className="mx-auto w-full max-w-3xl">
        <PageHeader title="Soporte" subtitle="Tu línea directa con tu gerente de cuenta." />
        <div className="mt-6 rounded-xl border border-line bg-surface p-6 text-center text-[12.5px] text-text-2">
          No pudimos cargar tus mensajes.{' '}
          <button
            type="button"
            onClick={() => void refetch()}
            className="font-semibold text-link hover:underline"
          >
            Reintentar
          </button>
        </div>
      </div>
    )
  }

  const { manager, messages } = data
  const managerName = manager?.name ?? 'tu gerente de cuenta'
  const firstName = manager?.name.split(' ')[0] ?? 'tu gerente'

  // Optimistic append: show the message immediately, keyed by a temp id, then let
  // the refetch (onSettled) reconcile with the server copy.
  function onSend(body: string): void {
    const optimistic: ConciergeMessage = {
      id: `tmp-${Date.now()}`,
      side: 'client',
      authorName: 'Tú',
      body,
      createdAt: new Date().toISOString(),
      // Not yet read by the manager: shows "Enviado" until a poll reports it read.
      read: false,
    }
    qc.setQueryData<typeof data>(['concierge', 'thread'], (cur) =>
      cur ? { ...cur, messages: [...cur.messages, optimistic] } : cur,
    )
    sendMutation.mutate(body)
  }

  return (
    <div data-testid="concierge" className="mx-auto w-full max-w-3xl">
      <PageHeader
        title="Soporte"
        subtitle="Tu línea directa con tu gerente de cuenta. Escríbele cuando lo necesites."
      />
      <div className="mt-5 h-[calc(100dvh-260px)] min-h-[420px] overflow-hidden rounded-xl border border-line bg-surface">
        <ConciergeThread
          messages={messages}
          mySide="client"
          headerName={managerName}
          headerSubtitle="Tu gerente de cuenta en INHERTA"
          headerInitials={manager ? conciergeInitials(manager.name) : 'V'}
          emptyText={`Tu gerente es ${managerName}. Escríbele cuando quieras.`}
          onSend={onSend}
          sending={sendMutation.isPending}
          disabled={manager === null}
          placeholder={`Escríbele a ${firstName}…`}
        />
      </div>
    </div>
  )
}
